import './MarketRunConfigFilters.scss';
import React, {forwardRef, ReactNode, useContext, useEffect, useImperativeHandle, useRef, useState, ChangeEvent, useCallback, useMemo} from "react";
import classnames from "classnames";
import ByzzerMarketSearch from "@/components/MarketSelection/ByzzerMarketSearch/ByzzerMarketSearch";
import {ReportRunConfig, RunConfigMarket} from "@/types/ReportRun";
import {
    ReportRunConfigWizardContext,
    ReportRunWizardState
} from "@/components/ConfigurationEditors/ReportConfigurationEditor/ReportRunConfigWizard/ReportRunConfigWizardContext";
import {MarketRunConfigOptions} from "@/types/RunConfigOptions";
import {FilterGroup, FilterSection} from '@/components/ConfigurationEditors/FilterGroup';
import OmniMarketSelect from '@/components/OmniMarketSelect/OmniMarketSelect';
import {MarketPicker} from "@/components/MarketPicker";
import {ByzzerInlineSelect, WizardStepContext, ByzzerTipIcon} from "@byzzer/ui-components";
import {LimitedLabel} from "@/components/LimitedLabel";
import {MarketTypeSelector} from '@/components/MarketSelection/MarketTypeSelector';
import {useMarketService} from "@/services/market.service";
import {useUser} from "@/contexts/UserContext";
import {LimitedMarketSelect} from "@/components/LimitedMarketSelect";
import {ByzzerSwitch} from "@/components/form";
import { SelectorLabelInfo } from '@/components/SelectorLabelInfo';
import { ByzzerOshMarketSelect } from '@/components/ByzzerOshMarketSelect';
import { PARENT_AND_CHILD_MARKET_REQUIRED_TIP, REQUIRED_FIELDS_NOT_FILLED_TIP } from '@/constants/market.constants';

export type MarketRunConfigFiltersValue = Partial<ReportRunConfig>;

export type MarketRunConfigFiltersProps =
    {
        name?: string;
        onValidityChange?: (e: ByzzerValidityChangeEvent) => void;
        handleDisabledTip?: (message: string) => void;
        /**
         * Used when MarketRunConfigFilters is used as controlled component.  Should be used along with the value prop.
         * This prop should be omitted when used within a ReportRunConfigWizardContext because the value in the context will
         * always take precedence.
         */
        onChange?: (e: ByzzerChangeEvent<MarketRunConfigFiltersValue>) => void;
        /**
         * Used when MarketRunConfigFilters is used as controlled component.  Should be used along with the onChange prop.
         */
        value?: MarketRunConfigFiltersValue;
        summary?: ReactNode;
        datatype?: any;
    }
    & Partial<Omit<MarketRunConfigOptions, 'title' | 'type'>>
    & Partial<Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>>;

export type MarketRunConfigFiltersRef = {
    value: MarketRunConfigFiltersValue;
    isValid: boolean;
};

const baseClassName = 'market-report-run-filters';

export const MarketRunConfigFilters = forwardRef<MarketRunConfigFiltersRef, MarketRunConfigFiltersProps>((
    {
        className,
        value,
        name,
        onChange,
        onValidityChange,
        handleDisabledTip,
        datatype,
        maxMarkets = Infinity,
        limitMarketsToParentCompany,
        allowMarketTypeSelection,
        includeTotalUSMarkets = false,
        includeFmcgRetailers = false,
        includeSpecialityRetailers = false,
        includeGeographyMarkets = false,
        includePanelTotal = false,
        includePanelChannel = false,
        includeOmniTotalMarkets = false,
        includeOmniSubMarkets = false,
        includeOmniShopperMarkets = false,
        excludeRemainingMarkets = false,
        requireRemainingMarket = false,
        /* BYZ-8104- we have added "hideTotalUSMarkets" flag to alby to hide and show the total US markets but at the same time we can use them as comparitive market if main market has US market as remaning or comparitive market.
         the same flag has been added in market.services to restrict the user from Total US markets search and market picker to wrap total US market tree with "hideTotalUSMarkets" flag. */
        hideTotalUSMarkets = false,

        requireTotalUSMarkets = false,
        requireFmcgRetailers = false,
        requireSpecialityRetailers = false,
        requireGeographyMarkets = false,
        requirePanelTotal = false,
        requirePanelChannel = false,
        requireOmniTotalMarkets = false,
        requireOmniSubMarkets = false,
        requireOmniShopperMarkets = false,

        maxOmniTotalMarkets,
        maxOmniSubMarkets,
        maxOmniShopperMarkets,
        excludeFocusMaket = false,
        marketTypeOptions, 
        ...props
    }, ref) => {

    const { company, features: userFeatures = {} } = useUser();
    const {value: contextValue, onChange: onContextChange, sku, runType} = useContext(ReportRunConfigWizardContext);
    const {showMessage, hideMessage} = useContext(WizardStepContext);
    const { getCachedMarketNodeByName, getChildMarketNodes, getMasterCompanyNodeByName } = useMarketService();
    const [internalValue, setInternalValue] = useState<MarketRunConfigFiltersValue>({});
    const [state, setState] = useState<ReportRunWizardState>({
        enableMarketTypePicker: allowMarketTypeSelection && Boolean(contextValue.markets?.length) ? false : allowMarketTypeSelection ? true : false,
        showRemainingMarkets: false
    });
    const [companyHasOnlyL1MarketsMap, setCompanyHasOnlyL1MarketsMap] = useState<Record<string, boolean>>({});

    const wasValid = useRef<boolean>(true); // switching this to true when disableInactiveStepRendering is set to true (in the wizard) since that completely rerenders the component and forgets its prior wasValid value, breaking the validation check
    const prevMarketsRef = useRef<RunConfigMarket[]>([]);
    const isOmni = datatype === 'omni';
    const isRms = datatype === 'rms';
    const isCps = datatype === 'cps';

    // todo: maybe this should be flag defined in Alby (but then again, maybe not)
    const limitMarketsToSameGroup = isCps;
    const showMarketTree = (isCps || isRms) && !state.enableMarketTypePicker;
    const showMarketSearch = showMarketTree;
    const showMarketTypePickers = state.enableMarketTypePicker;
    const categoriesRefreshKey = `${contextValue.categories?.sort().join()}`
    const parentAndChildSelectionRequiredSku = ['491'];

    /** Atleast one parent and one child selection is required in Retailer Landscape Report for now i.e BYZ-15372 */
    const isParentAndChildMarketSelectionRequired = useMemo((): boolean => {
        return Boolean(limitMarketsToParentCompany && parentAndChildSelectionRequiredSku.includes(sku!));
    }, [limitMarketsToParentCompany, sku]);

    useEffect(() => {        
        handleMapMasterCompanies(contextValue?.markets ?? []);
    }, []);

    useEffect(() => {

        const isValid = checkValidity();

        if (wasValid.current !== isValid) {
            onValidityChange?.({
                name,
                isValid
            });

            wasValid.current = isValid;
        }

        const message = getValidationMessage();
        handleDisabledTip?.(message);

    }, [internalValue.categories, internalValue.markets, internalValue.comparisonMarket, internalValue.subMarkets]);

    // this could be problematic and lead to issues where the context and explicit value collide.
    // todo: find a clear way to choose which takes precedence context or value
    useEffect(() => {
        setInternalValue(contextValue ?? {})
    }, [contextValue]);

    useEffect(() => {
        if (value) {
            setInternalValue(value ?? {});
        }
    }, [value]);

    useEffect(() => {

        const firstMarket = contextValue.markets?.[0];
        const requiredMasterCompany = limitMarketsToParentCompany ? firstMarket?.masterCompany : undefined;
        const requiredMarketGroup = limitMarketsToSameGroup ? firstMarket?.marketGroup : undefined;
        const isMarketsChanged = JSON.stringify(contextValue.markets) !== JSON.stringify(prevMarketsRef.current);

        setState(state => ({
            ...state,
            requiredMasterCompany,
            requiredMarketGroup,
        }));
        
        dispatchParentCompaniesInContext();
        
        if (isMarketsChanged) {
            prevMarketsRef.current = contextValue.markets ?? []; // Update previous markets
            filterDisabledMarketsInContext();
        }
    }, [contextValue.markets]);

    /**
     * This purpose of this function is related to BYZ-10500.
     * When there is market selected which is a custom market means the market having isParentCompany
     * is true then have to create a parentCompany array which will contain all the selected market names
     * so that we can send it as a payload in validate API.
     */
    function dispatchParentCompaniesInContext() {
        const parentCompanies: string[] = [];
        let hasParentCompany: boolean = false;
    
        if (Array.isArray(contextValue.markets)) {
            for (const mrkt of contextValue.markets) {
                if (mrkt.isParentCompany) {
                    hasParentCompany = true;
                }
                parentCompanies.push(mrkt.name);
            }
        }
    
        if (hasParentCompany) {
            onContextChange?.('parentCompany', parentCompanies);
        } else {
            onContextChange?.('parentCompany', []);
        }
    }

    function filterDisabledMarketsInContext() {
        onContextChange?.('markets', contextValue.markets?.filter(mrkt => !mrkt.disabledMarketNode) ?? []);
    }

    function handleChange(e: ByzzerChangeEvent<any>) {        
        onContextChange?.(e.name as keyof ReportRunConfig, e.value, e.data);
        handleMapMasterCompanies(e.value);
    }

    function handleMarketTypeChange(e: ByzzerChangeEvent<boolean>) {

        onContextChange?.('markets', [], '');
        onContextChange?.('comparisonMarket', undefined, '');
        setState(state => ({
            ...state,
            [e.name!]: e.value
        }))
    }

    function getTopLevelPath(path: string) : string {
        const normalizePath = (p: string) => p.replace(/\\/g, '/');
        const mrktPathRoot = normalizePath(path).split('/')[0];

        return mrktPathRoot;
    }

    function handleCompanyHasOnlyL1MarketsCheck(masterCompany: string, hasOnlyL1: boolean, path: string) {
        setCompanyHasOnlyL1MarketsMap((prev) => ({ ...prev, [getCustomisedL1CompanyPath(path, masterCompany)]: hasOnlyL1 }));
    }

    function getCustomisedL1CompanyPath(path: string, masterCompany: string) {
        return !path || !masterCompany ? '' : `${getTopLevelPath(path)}${masterCompany}`;
    }

    /**
     * Validates whether the selected master company only has L1 (top-level) markets.
     * 
     * This function is needed **only** when:
     * The selected master company is **not already present** in the `companyHasOnlyL1MarketsMap`.
     *
     * In most cases, this map is populated during node interaction in MarketPickerNode.
     * However, if a user skips the picker and directly searches using MarketSearch,
     * the map may not be updated yet — so this function ensures we backfill it.
     */
    const handleMapMasterCompanies = async (selectedMarkets: RunConfigMarket[]) => {
        // Only trigger this logic if parent and child selection is required
        if (!isParentAndChildMarketSelectionRequired) return;

        if (!selectedMarkets?.length) return;

        const selectedMarket = selectedMarkets[0];
        const masterCompany = selectedMarket?.masterCompany;
        
        if (!masterCompany) return;
        
        const cachedMasterCompanyKeyName = getCustomisedL1CompanyPath(selectedMarket?.path, masterCompany!);

        // Skip if we've already determined whether this company has only L1 markets
        if (cachedMasterCompanyKeyName in companyHasOnlyL1MarketsMap) return;

        const marketNode = getMasterCompanyNodeByName(
            selectedMarket.masterCompany!, // Picking market node by masterCompany to get the masterCompany node only
            selectedMarket.path, // important: use path if available for accurate match
        );

        if (!marketNode) return;

        const childNodes = await getChildMarketNodes({
            parent: marketNode,
            sku: sku!,
            categories: contextValue.categories
        });

        const hasOnlyL1Markets = childNodes.every(child => !child.hasChildren);

        // Store result so we don't compute again for this master company
        handleCompanyHasOnlyL1MarketsCheck(masterCompany, hasOnlyL1Markets, selectedMarket?.path);
    }

    /** This will ensure that one parent and one child is selected under the same master company */
    const hasParentAndChildMarketSelected = (): boolean => {
        let isParentSelected = false;
        let isChildSelected = false;

        const selectedMarkets = internalValue.markets ?? [];
        const cachedMasterCompanyPath = getCustomisedL1CompanyPath(selectedMarkets?.[0]?.path, state?.requiredMasterCompany!)
        const hasOnlyL1 = cachedMasterCompanyPath ? companyHasOnlyL1MarketsMap[cachedMasterCompanyPath] : false;

        // If it's known that this master company has only L1 markets, relax validation
        if (hasOnlyL1) return true;

        for (const { name, path } of selectedMarkets) {
            const { masterCompany: mCompany, marketLevel } = getCachedMarketNodeByName(name, undefined, false, false, undefined, undefined, false, path) ?? {};
            if (mCompany === state?.requiredMasterCompany) {
                if (marketLevel === 'Level 1') isParentSelected = true;
                else isChildSelected = true;

                if (isParentSelected && isChildSelected) return true;
            }
        }

        return false;
    };

    const checkValidity = useCallback((): boolean => {
        const hasMarketType = Boolean(internalValue.comparisonMarket?.channel?.length);
        const hasMarkets = Boolean(internalValue.markets?.length);
        const hasSubMarkets = Boolean(internalValue.subMarkets?.length);
        const requiresMarketType = state.enableMarketTypePicker;
        const requiresStandardMarkets = (isRms || isCps) && !state.enableMarketTypePicker;
        const requiresMarkets = requiresStandardMarkets || requireOmniTotalMarkets;

        // Remaining markets are now paired/bundled with their base market.  Reports from history don't need to be checked.

        const marketTypeResult = !requiresMarketType || hasMarketType;
        const marketsResult = !requiresMarkets || hasMarkets;
        const omniSubMarketsResult = !requireOmniSubMarkets || hasSubMarkets;
        const oshMarketsResult = !requireOmniShopperMarkets || hasMarkets || hasSubMarkets;
        
        // Check the functions for more clarity
        const parentChildSelectionResult = isParentAndChildMarketSelectionRequired ? hasParentAndChildMarketSelected() : true;

        const result = marketTypeResult && marketsResult && omniSubMarketsResult && oshMarketsResult && parentChildSelectionResult;

        return result;
    }, [
        internalValue.comparisonMarket?.channel,
        internalValue.markets,
        internalValue.subMarkets,
        state.enableMarketTypePicker,
        isRms,
        isCps,
        requireOmniTotalMarkets,
        requireOmniSubMarkets,
        requireOmniShopperMarkets,
    ]);
    
    const getValidationMessage = (): string => {
        if (internalValue.markets?.length && isParentAndChildMarketSelectionRequired) {
            if (!hasParentAndChildMarketSelected()) {
                return PARENT_AND_CHILD_MARKET_REQUIRED_TIP;
            }
        }

        return REQUIRED_FIELDS_NOT_FILLED_TIP;
    }

    function handleIncludeRemainingChange(e: ChangeEvent<HTMLInputElement>) {
        setState(state => ({
            ...state,
            showRemainingMarkets: e.target.checked
        }))
    }

    useImperativeHandle(ref, () => ({
        get value() {
            return internalValue;
        },
        get isValid() {
            return checkValidity();
        },
    }));

    return (
        <FilterGroup className={classnames(baseClassName, className)}>
            <FilterSection onlyRenderIf={isCps || isRms}>
                {Boolean(allowMarketTypeSelection) && (
                    <>
                        <h1>What type of comparison is right for you?</h1>
                        {!Boolean(excludeFocusMaket) && (
                            <h3>
                                We will show you your top 20 markets based on the analysis type you select or &nbsp;
                                <ByzzerInlineSelect<boolean>
                                    name={'enableMarketTypePicker'}
                                    onChange={handleMarketTypeChange}
                                    value={state.enableMarketTypePicker}
                                    options={[
                                        { display: 'select by market type', value: true },
                                        { display: 'select a custom list of markets', value: false },
                                    ]}
                                />
                            </h3>
                        )}
                    </>
                )}

                {!allowMarketTypeSelection && !requireRemainingMarket && (
                    <SelectorLabelInfo
                        sku={sku as string}
                        selectorCode={'focusMarkets'}
                        max={maxMarkets}
                        isLabelBold={true}
                    />
                )}

                {requireRemainingMarket && (
                    <SelectorLabelInfo
                        sku={sku as string}
                        selectorCode={'remainingMarkets'}
                        max={maxMarkets}
                        isLabelBold={true}
                    />
                )}

                {showMarketTypePickers && (
                    <MarketTypeSelector
                        name="comparisonMarket"
                        value={internalValue.comparisonMarket}
                        onChange={handleChange}
                        sku={sku}
                        marketTypeOptions={marketTypeOptions}
                    />
                )}

                {showMarketTree && !excludeRemainingMarkets && !requireRemainingMarket && (
                    <ByzzerSwitch
                        className={`${baseClassName}__remaining-market-toggle`}
                        onChange={handleIncludeRemainingChange}
                    >
                        <SelectorLabelInfo
                            sku={sku as string}
                            selectorCode={'remainingMarketToggle'}
                            tipDelay={[150, 0]}
                        />
                    </ByzzerSwitch>
                )}

                {showMarketSearch && (
                    <ByzzerMarketSearch
                        name={'markets'}
                        productSku={sku}
                        categories={contextValue.categories}
                        requiredMasterCompany={state.requiredMasterCompany}
                        requiredMarketGroup={state.requiredMarketGroup}
                        requireRemainingMarket={requireRemainingMarket}
                        showRemainingMarkets={state.showRemainingMarkets}
                        value={contextValue.markets}
                        maxSelections={maxMarkets}
                        onChange={handleChange}
                    />
                )}

                {showMarketTree && (
                    <MarketPicker
                        key={categoriesRefreshKey}
                        value={contextValue.markets}
                        onChange={handleChange}
                        maxSelections={maxMarkets}
                        categories={contextValue.categories}
                        requiredMasterCompany={state.requiredMasterCompany}
                        requiredMarketGroup={state.requiredMarketGroup}
                        requireRemainingMarket={requireRemainingMarket}
                        name={'markets'}
                        productSku={sku}
                        runType={runType}
                        hideTotalUSMarkets={hideTotalUSMarkets}
                        showRemainingMarkets={state.showRemainingMarkets}
                        displayMarketGroup={datatype}
                        purchasedMarketKeys={company?.purchasedMarketKeys}
                        enableParentMarket={datatype === 'cps'}
                        filterType="all"
                        isParentAndChildMarketSelectionRequired={isParentAndChildMarketSelectionRequired}
                        onCompanyHasOnlyL1MarketsCheck={handleCompanyHasOnlyL1MarketsCheck}
                    />
                )}
            </FilterSection>

            <FilterSection onlyRenderIf={includeOmniTotalMarkets}>
                <SelectorLabelInfo
                    sku={sku as string}
                    selectorCode={'omniTotalMarkets'}
                    required={requireOmniTotalMarkets}
                    max={maxOmniTotalMarkets}
                    isLabelBold={true}
                />
                <OmniMarketSelect
                    name={'markets'}
                    value={internalValue.markets}
                    onChange={handleChange}
                    placeholder={'Select Total Markets'}
                    categories={internalValue.categories!}
                    runType={runType!}
                    allowMultiple={Number(maxOmniTotalMarkets) > 0}
                    maxSelections={maxOmniTotalMarkets}
                />
            </FilterSection>

            <FilterSection onlyRenderIf={includeOmniSubMarkets}>
                <h1 className={`filter-group-section__label`}>
                    <>
                        <SelectorLabelInfo
                            sku={sku as string}
                            selectorCode={'omniSubMarkets'}
                            required={requireOmniTotalMarkets}
                            max={maxOmniTotalMarkets}
                        />
                    </>
                </h1>
                {/* <h3>
                    The Sub-Market will be reported to understand how the performance in the sub-market is driving
                    overall performance for the total market. It's recommended that you choose the online
                    market beneath the total market for this selection. For example if you chose a Total Retailer
                    for the prior selection, here you would choose the Online market of the same retailer.
                </h3> */}
                <OmniMarketSelect
                    name={"subMarkets"}
                    value={internalValue.subMarkets}
                    onChange={handleChange}
                    placeholder={"Select Sub-Markets"}
                    categories={internalValue.categories!}
                    runType={runType!}
                    allowMultiple={Number(maxOmniSubMarkets) > 0}
                    maxSelections={maxOmniSubMarkets}
                />
            </FilterSection>

            <ByzzerOshMarketSelect
                includeOmniShopperMarkets={includeOmniShopperMarkets}
                sku={sku as string}
                value={internalValue.markets ?? []}
                onChange={handleChange}
                name={'markets'}
                maxSelections={maxOmniShopperMarkets}
            />
        </FilterGroup>
    );
})

MarketRunConfigFilters.displayName = 'MarketRunConfigFilters'